import React, { useState } from 'react';
import { Eye, Search, Filter, Edit, Trash2, User, Phone, Calendar, UserCheck, X, Mail, MapPin, CreditCard, Clock } from 'lucide-react';

const ViewEmployee = ({ onBack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [employees, setEmployees] = useState([
    {
      id: 1,
      firstName: 'John',
      lastName: 'Doe',
      phoneNumber: '+1234567890',
      dateOfBirth: '1990-05-15',
      username: 'johndoe',
      email: 'john.doe@company.com',
      employeeType: 'staff',
      joinDate: '2023-01-15',
      status: 'active',
      address: '123 Main St, New York, NY 10001',
      employeeId: 'EMP-001',
      lastLogin: '2023-10-20T09:45:32'
    },
    {
      id: 2,
      firstName: 'Jane',
      lastName: 'Smith',
      phoneNumber: '+1987654321',
      dateOfBirth: '1985-08-22',
      username: 'janesmith',
      email: 'jane.smith@company.com',
      employeeType: 'accountant',
      joinDate: '2022-11-30',
      status: 'active',
      address: '456 Oak Ave, Los Angeles, CA 90001',
      employeeId: 'EMP-002',
      lastLogin: '2023-10-21T14:22:18'
    },
    {
      id: 3,
      firstName: 'Robert',
      lastName: 'Johnson',
      phoneNumber: '+1122334455',
      dateOfBirth: '1992-03-08',
      username: 'robertj',
      email: 'robert.johnson@company.com',
      employeeType: 'operator',
      joinDate: '2023-05-20',
      status: 'inactive',
      address: '789 Pine Rd, Chicago, IL 60601',
      employeeId: 'EMP-003',
      lastLogin: '2023-09-15T11:30:45'
    },
    {
      id: 4,
      firstName: 'Sarah',
      lastName: 'Wilson',
      phoneNumber: '+1555666777',
      dateOfBirth: '1988-12-14',
      username: 'sarahw',
      email: 'sarah.wilson@company.com',
      employeeType: 'staff',
      joinDate: '2022-08-10',
      status: 'active',
      address: '321 Elm St, Houston, TX 77001',
      employeeId: 'EMP-004',
      lastLogin: '2023-10-22T08:15:29'
    }
  ]);

  const employeeTypes = [
    { id: 'all', label: 'All Types' },
    { id: 'staff', label: 'Staff' },
    { id: 'operator', label: 'Operator' },
    { id: 'accountant', label: 'Accountant' },
  ];

  const statusOptions = [
    { id: 'all', label: 'All Status' },
    { id: 'active', label: 'Active' },
    { id: 'inactive', label: 'Inactive' }
  ];

  const [alertModal, setAlertModal] = useState({ 
    isOpen: false, 
    type: '', 
    employee: null, 
    message: '' 
  });

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatDateTime = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getEmployeeTypeLabel = (type) => {
    const found = employeeTypes.find(t => t.id === type);
    return found ? found.label : type;
  };

  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = 
      employee.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.phoneNumber.includes(searchTerm);
    
    const matchesType = filterType === 'all' || employee.employeeType === filterType;
    const matchesStatus = filterStatus === 'all' || employee.status === filterStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const handleEdit = (employee) => {
    setAlertModal({
      isOpen: true,
      type: 'edit',
      employee,
      message: `You are about to edit ${employee.firstName} ${employee.lastName}'s details.`
    });
  };

  const handleDelete = (employee) => {
    setAlertModal({
      isOpen: true,
      type: 'delete',
      employee,
      message: `Are you sure you want to delete ${employee.firstName} ${employee.lastName}? This action cannot be undone.`
    });
  };

  const handleView = (employee) => {
    setAlertModal({
      isOpen: true,
      type: 'view',
      employee,
      message: `${employee.firstName} ${employee.lastName}'s Details`
    });
  };

  const toggleStatus = (employee) => {
    setAlertModal({
      isOpen: true,
      type: 'toggleStatus',
      employee,
      message: `Are you sure you want to ${employee.status === 'active' ? 'deactivate' : 'activate'} ${employee.firstName} ${employee.lastName}?`
    });
  };

  const confirmAction = () => {
    const { type, employee } = alertModal;
    
    if (type === 'edit') {
      console.log('Edit employee:', employee);
      // Here you would typically open an edit form or navigate to an edit page
    } else if (type === 'delete') {
      // Remove the employee from the state
      setEmployees(prevEmployees => 
        prevEmployees.filter(emp => emp.id !== employee.id)
      );
    } else if (type === 'toggleStatus') {
      // Toggle employee status
      setEmployees(prevEmployees => 
        prevEmployees.map(emp => 
          emp.id === employee.id 
            ? { ...emp, status: emp.status === 'active' ? 'inactive' : 'active' } 
            : emp
        )
      );
    }
    
    // Close the modal
    setAlertModal({ isOpen: false, type: '', employee: null, message: '' });
    
    // Show success message for delete or status change
    if (type === 'delete' || type === 'toggleStatus') {
      setTimeout(() => {
        setAlertModal({
          isOpen: true,
          type: 'success',
          employee: null,
          message: type === 'delete' 
            ? `Employee ${employee.firstName} ${employee.lastName} has been successfully deleted.`
            : `Employee ${employee.firstName} ${employee.lastName} has been ${employee.status === 'active' ? 'deactivated' : 'activated'}.`
        });
      }, 300);
    }
  };

  const closeModal = () => {
    setAlertModal({ isOpen: false, type: '', employee: null, message: '' });
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-blue-600 text-white p-6 flex items-center justify-between">
          <div className="flex items-center">
            <button 
              onClick={onBack}
              className="mr-4 p-1 rounded-full hover:bg-blue-500 transition-colors"
            >
              <Eye className="w-6 h-6" />
            </button>
            <div>
              <h2 className="text-2xl font-bold">View Employees</h2>
              <p className="text-blue-100">Manage your employee accounts</p>
            </div>
          </div>
          <UserCheck className="w-8 h-8" />
        </div>

        <div className="p-6">
          {/* Filters and Search */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search employees by name, username or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {employeeTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {statusOptions.map(status => (
                    <option key={status.id} value={status.id}>{status.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Employees Table */}
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Join Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredEmployees.length > 0 ? (
                  filteredEmployees.map((employee) => (
                    <tr key={employee.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                            <User className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {employee.firstName} {employee.lastName}
                            </div>
                            <div className="text-sm text-gray-500">@{employee.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-900">
                          <Phone className="w-4 h-4 mr-2 text-gray-400" />
                          {employee.phoneNumber}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          DOB: {formatDate(employee.dateOfBirth)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          {getEmployeeTypeLabel(employee.employeeType)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatDate(employee.joinDate)}
                      </td>
                      <td className="px-6 py-4">
                        <button 
                          onClick={() => toggleStatus(employee)}
                          className={`px-2 py-1 text-xs font-medium rounded-full cursor-pointer ${
                            employee.status === 'active'
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                        >
                          {employee.status.charAt(0).toUpperCase() + employee.status.slice(1)}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleView(employee)}
                            className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors"
                            title="View employee"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(employee)}
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                            title="Edit employee"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(employee)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                            title="Delete employee"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center">
                      <div className="text-gray-500">
                        <User className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg font-medium">No employees found</p>
                        <p className="text-sm">Try adjusting your search or filter criteria</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredEmployees.length > 0 && (
            <div className="flex items-center justify-between mt-6 px-6 py-3 bg-gray-50 border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">1</span> to <span className="font-medium">{filteredEmployees.length}</span> of{' '}
                <span className="font-medium">{filteredEmployees.length}</span> results
              </div>
              <div className="flex space-x-2">
                <button className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">
                  Previous
                </button>
                <button className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50">
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Alert Modal */}
      {alertModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`bg-white rounded-lg shadow-xl max-w-2xl w-full ${alertModal.type === 'view' ? 'max-w-4xl' : 'max-w-md'}`}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                {alertModal.type === 'edit' && 'Edit Employee'}
                {alertModal.type === 'delete' && 'Delete Employee'}
                {alertModal.type === 'view' && 'Employee Details'}
                {alertModal.type === 'success' && 'Success'}
                {alertModal.type === 'toggleStatus' && 'Change Status'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {alertModal.type !== 'view' && <p className="mb-4">{alertModal.message}</p>}
              
              {alertModal.type === 'view' && alertModal.employee && (
                <div>
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                      <User className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        {alertModal.employee.firstName} {alertModal.employee.lastName}
                      </h2>
                      <p className="text-gray-500">@{alertModal.employee.username}</p>
                    </div>
                    <span className={`ml-auto px-3 py-1 text-xs font-medium rounded-full ${
                      alertModal.employee.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {alertModal.employee.status.charAt(0).toUpperCase() + alertModal.employee.status.slice(1)}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-blue-800 mb-3 flex items-center">
                        <CreditCard className="w-5 h-5 mr-2" />
                        Personal Information
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-blue-600">Employee ID</p>
                          <p className="font-medium">{alertModal.employee.employeeId}</p>
                        </div>
                        <div>
                          <p className="text-sm text-blue-600">Date of Birth</p>
                          <p className="font-medium">{formatDate(alertModal.employee.dateOfBirth)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-blue-600">Employee Type</p>
                          <p className="font-medium">{getEmployeeTypeLabel(alertModal.employee.employeeType)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-green-800 mb-3 flex items-center">
                        <Phone className="w-5 h-5 mr-2" />
                        Contact Information
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-green-600">Phone Number</p>
                          <p className="font-medium">{alertModal.employee.phoneNumber}</p>
                        </div>
                        <div>
                          <p className="text-sm text-green-600">Email Address</p>
                          <p className="font-medium flex items-center">
                            <Mail className="w-4 h-4 mr-1" />
                            {alertModal.employee.email}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-green-600">Address</p>
                          <p className="font-medium flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            {alertModal.employee.address}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-purple-800 mb-3 flex items-center">
                        <Calendar className="w-5 h-5 mr-2" />
                        Employment Details
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-purple-600">Join Date</p>
                          <p className="font-medium">{formatDate(alertModal.employee.joinDate)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-purple-600">Last Login</p>
                          <p className="font-medium flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {formatDateTime(alertModal.employee.lastLogin)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 p-4 border-t">
              {alertModal.type === 'success' ? (
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  OK
                </button>
              ) : alertModal.type === 'view' ? (
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Close
                </button>
              ) : (
                <>
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmAction}
                    className={`px-4 py-2 text-white rounded-md ${
                      alertModal.type === 'delete' 
                        ? 'bg-red-600 hover:bg-red-700' 
                        : alertModal.type === 'toggleStatus'
                        ? 'bg-orange-600 hover:bg-orange-700'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {alertModal.type === 'delete' 
                      ? 'Delete' 
                      : alertModal.type === 'toggleStatus'
                      ? 'Confirm Change'
                      : 'Confirm'
                    }
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ViewEmployee;